/**
 * Core location definitions for Idle Depths.
 *
 * Defines the initial game world as a graph of connected locations.
 * Travel times are in milliseconds, designed to be fast (2-5 seconds).
 */

import { type LocationDefinition, isLocationDefinition } from '@idle-depths/shared';

export { isLocationDefinition };

/**
 * Core location definitions.
 *
 * Forms a triangle with varying travel times:
 *
 * ```
 *                    Mountain Pass
 *                        /\
 *                   5s /    \ 2s
 *                     /      \
 *     Coastal Village -------- Darkwood Forest
 *                       3s
 * ```
 *
 * - Coastal Village: Starting location (fishing, basic shops)
 * - Darkwood Forest: Gathering, hunting
 * - Mountain Pass: Mining, danger
 */
export const CORE_LOCATIONS: LocationDefinition[] = [
  {
    id: 'coastal_village',
    name: 'Coastal Village',
    description:
      'A peaceful fishing village on the shores of the Shattered Coast. The salty breeze carries the sound of gulls and crashing waves.',
    icon: 'Anchor',
    theme: 'coastal',
    unlockRequirements: {},
    connections: [
      { toLocationId: 'darkwood_forest', travelTimeMs: 3000 }, // 3 seconds
      { toLocationId: 'mountain_pass', travelTimeMs: 5000 }, // 5 seconds (longer route)
    ],
    isStartingLocation: true,
  },
  {
    id: 'darkwood_forest',
    name: 'Darkwood Forest',
    description:
      'A dense forest with towering trees and mysterious shadows. Strange sounds echo through the undergrowth.',
    icon: 'Trees',
    theme: 'forest',
    unlockRequirements: {},
    connections: [
      { toLocationId: 'coastal_village', travelTimeMs: 3000 }, // 3 seconds (same as going there)
      { toLocationId: 'mountain_pass', travelTimeMs: 2000 }, // 2 seconds (short forest path)
    ],
  },
  {
    id: 'mountain_pass',
    name: 'Mountain Pass',
    description:
      'A treacherous path through the northern mountains. The air is thin and cold, but the view is breathtaking.',
    icon: 'Mountain',
    theme: 'mountain',
    unlockRequirements: {},
    connections: [
      { toLocationId: 'darkwood_forest', travelTimeMs: 2000 }, // 2 seconds
      { toLocationId: 'coastal_village', travelTimeMs: 5000 }, // 5 seconds (long descent)
    ],
  },
];
